import { useState, useEffect } from 'react'
import { Monitor, Smartphone, Tablet, MapPin, Clock, Shield, LogOut } from 'lucide-react'
import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api'

export default function SessionsSettings() {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchSessions()
  }, [])

  const fetchSessions = async () => {
    try {
      // TODO: Implémenter l'endpoint backend pour récupérer les sessions
      // Pour l'instant, données de démonstration
      const mockSessions = [
        {
          id: 1,
          device: 'Chrome sur Windows',
          deviceType: 'desktop',
          location: 'Paris, France',
          ip: '192.168.1.1',
          lastActive: new Date(),
          isCurrent: true
        },
        {
          id: 2,
          device: 'Safari sur iPhone',
          deviceType: 'mobile',
          location: 'Lyon, France',
          ip: '192.168.1.2',
          lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000),
          isCurrent: false
        }
      ]
      setSessions(mockSessions)
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogoutSession = async (sessionId) => {
    if (!confirm('Êtes-vous sûr de vouloir déconnecter cette session ?')) return

    try {
      // TODO: Implémenter l'endpoint backend
      // await axios.post(`${API_URL}/auth/logout-session/${sessionId}`, {}, {
      //   headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      // })
      setSessions(sessions.filter(s => s.id !== sessionId))
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error)
      alert('Erreur lors de la déconnexion de la session')
    }
  }

  const handleLogoutAllOthers = async () => {
    if (!confirm('Déconnecter toutes les autres sessions ?')) return

    try {
      // TODO: Implémenter l'endpoint backend
      setSessions(sessions.filter(s => s.isCurrent))
    } catch (error) {
      console.error('Erreur:', error)
      alert('Erreur lors de la déconnexion des sessions')
    }
  }

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone className="h-5 w-5" />
      case 'tablet':
        return <Tablet className="h-5 w-5" />
      default:
        return <Monitor className="h-5 w-5" />
    }
  }

  const formatLastActive = (date) => {
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (minutes < 1) return 'À l\'instant'
    if (minutes < 60) return `Il y a ${minutes} min`
    if (hours < 24) return `Il y a ${hours}h`
    return `Il y a ${days}j`
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="card-header">
          <div>
            <h3 className="card-title">Sessions actives</h3>
            <p className="card-subtitle">Gérez les appareils connectés à votre compte</p>
          </div>
          <button
            className="btn btn-danger-outline"
            onClick={handleLogoutAllOthers}
            disabled={sessions.filter(s => !s.isCurrent).length === 0}
          >
            <LogOut className="h-4 w-4" />
            Déconnecter les autres
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card card-kpi">
          <div className="kpi-label">Sessions actives</div>
          <div className="kpi-value">{sessions.length}</div>
        </div>

        <div className="card card-kpi">
          <div className="kpi-label">Dernière connexion</div>
          <div className="kpi-value text-sm">
            {formatLastActive(sessions[0]?.lastActive || new Date())}
          </div>
        </div>

        <div className="card card-kpi">
          <div className="kpi-label">Sécurité</div>
          <div className="flex items-center gap-2 text-green-600 mt-2">
            <Shield className="h-5 w-5" />
            <span className="font-medium">Protégé</span>
          </div>
        </div>
      </div>

      {/* Liste des sessions */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Appareils connectés</h3>
        </div>
        <div className="divide-y">
          {loading ? (
            <div className="p-6 text-center text-muted">Chargement...</div>
          ) : sessions.length === 0 ? (
            <div className="p-6 text-center text-muted">Aucune session active</div>
          ) : (
            sessions.map((session) => (
              <div key={session.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                        {getDeviceIcon(session.deviceType)}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{session.device}</h4>
                        {session.isCurrent && (
                          <span className="badge badge-success">Session actuelle</span>
                        )}
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-muted">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span>{session.location}</span>
                          <span className="text-gray-400">•</span>
                          <span>{session.ip}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          <span>{formatLastActive(session.lastActive)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {!session.isCurrent && (
                    <button
                      onClick={() => handleLogoutSession(session.id)}
                      className="btn btn-sm btn-danger-outline"
                    >
                      <LogOut className="h-4 w-4" />
                      Déconnecter
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Info sécurité */}
      <div className="card bg-blue-50 border border-blue-200">
        <div className="p-6">
          <div className="flex gap-3">
            <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-1">Sécurité de votre compte</h4>
              <p className="text-sm text-blue-700">
                Si vous remarquez une activité suspecte ou un appareil que vous ne reconnaissez pas,
                déconnectez-le immédiatement et changez votre mot de passe.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
